import React, { useState } from 'react';
import { Fingerprint, ShieldCheck, ShieldAlert, CheckCircle, RefreshCw, Smartphone } from 'lucide-react';
import { useProfileSecurity } from '../../hooks/useProfileSecurity';
import TwoFactorEnrollment from '../TwoFactorEnrollment';

/**
 * Security settings — passkeys and two-factor authentication.
 *
 * Everything shown here is read from Hanko's `/profile` flow, never assumed.
 * This panel used to print "your account is protected with 2FA" as fixed text,
 * which was false for every account that never finished enrollment: Hanko asks
 * for a code only when the user actually holds a TOTP secret, so those accounts
 * signed in on a password alone while this screen claimed a second factor.
 */
export function AccountSecuritySection() {
    const {
        loading, busy, error, notice, enroll, code, setCode, setError,
        registered, passkeyCount,
        registerPasskey, startEnrollment, confirmEnrollment, cancelEnrollment,
    } = useProfileSecurity();

    const [confirmRescan, setConfirmRescan] = useState(false);

    const beginRescan = async () => {
        setConfirmRescan(false);
        await startEnrollment();
    };

    return (
        <div>
            <div style={styles.header}>
                <h2 style={styles.title}>Security Settings</h2>
                <p style={styles.subtitle}>Manage your personal authentication methods.</p>
            </div>

            {error && <p style={styles.error}>{error}</p>}
            {notice && (
                <div style={styles.successBanner}>
                    <CheckCircle size={16} style={{ flexShrink: 0 }} />
                    <span>{notice}</span>
                </div>
            )}

            {/* ── Two-factor authentication ── */}
            <div style={styles.card}>
                <div style={styles.sectionHeader}>
                    {registered
                        ? <ShieldCheck size={22} style={{ color: '#4ADE80', marginTop: 2, flexShrink: 0 }} />
                        : <ShieldAlert size={22} style={{ color: '#FBBF24', marginTop: 2, flexShrink: 0 }} />}
                    <div>
                        <h3 style={styles.sectionTitle}>Two-Factor Authentication</h3>
                        <p style={styles.sectionDescription}>
                            A 6-digit code from an authenticator app, asked for after your
                            password. Look for "SAM Surveyor" in the app.
                        </p>
                    </div>
                </div>

                {registered === null ? (
                    <div style={styles.infoBanner}>
                        {loading ? 'Checking your account…' : 'Your two-factor status could not be read.'}
                    </div>
                ) : registered ? (
                    <div style={styles.okBanner}>
                        <CheckCircle size={15} style={{ flexShrink: 0 }} />
                        <span>Registered. You are asked for a code when you sign in with a password.</span>
                    </div>
                ) : (
                    <div style={styles.warnBanner}>
                        <ShieldAlert size={15} style={{ flexShrink: 0 }} />
                        <span>
                            Not registered. Your password alone signs you in — nothing asks
                            you for a second factor. Register below to change that.
                        </span>
                    </div>
                )}

                <TwoFactorEnrollment
                    enroll={enroll}
                    code={code}
                    setCode={setCode}
                    busy={busy}
                    onConfirm={confirmEnrollment}
                    onCancel={cancelEnrollment}
                />

                {/* Re-scanning destroys the working enrollment before a new one
                    is issued, so it asks first. */}
                {!enroll && confirmRescan && (
                    <div style={styles.warnBanner}>
                        <ShieldAlert size={15} style={{ flexShrink: 0 }} />
                        <div>
                            <p style={{ margin: '0 0 10px' }}>
                                Re-scanning replaces your current authenticator entry. The old
                                one stops working straight away, and your account has no second
                                factor until you confirm the new code.
                            </p>
                            <button onClick={beginRescan} disabled={busy === 'totp'} style={styles.registerBtn}>
                                {busy === 'totp' ? 'Starting…' : 'Continue'}
                            </button>
                            <button
                                onClick={() => { setConfirmRescan(false); setError(''); }}
                                style={styles.secondaryBtn}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {!enroll && !confirmRescan && (
                    <button
                        onClick={registered ? () => setConfirmRescan(true) : startEnrollment}
                        disabled={loading || busy === 'totp'}
                        style={{
                            ...styles.registerBtn,
                            ...(loading || busy === 'totp' ? styles.registerBtnDisabled : {}),
                        }}
                    >
                        {registered ? <RefreshCw size={15} /> : <Smartphone size={15} />}
                        {busy === 'totp' ? 'Starting…' : registered ? 'Re-scan 2FA' : 'Register 2FA'}
                    </button>
                )}
            </div>

            {/* ── Passkeys ── */}
            <div style={{ ...styles.card, marginTop: 20 }}>
                <div style={styles.sectionHeader}>
                    <Fingerprint size={22} style={{ color: '#60A5FA', marginTop: 2, flexShrink: 0 }} />
                    <div>
                        <h3 style={styles.sectionTitle}>Passkeys</h3>
                        <p style={styles.sectionDescription}>
                            Sign in with Touch ID, Face ID, Windows Hello, or your password
                            manager — no password or 2FA code needed.
                        </p>
                    </div>
                </div>

                {!loading && (
                    <div style={passkeyCount ? styles.okBanner : styles.infoBanner}>
                        {passkeyCount
                            ? `${passkeyCount} passkey${passkeyCount === 1 ? '' : 's'} registered on this account.`
                            : 'No passkeys registered yet.'}
                    </div>
                )}

                <button
                    onClick={registerPasskey}
                    disabled={loading || busy === 'passkey'}
                    style={{
                        ...styles.registerBtn,
                        ...(loading || busy === 'passkey' ? styles.registerBtnDisabled : {}),
                    }}
                >
                    <Fingerprint size={15} />
                    {busy === 'passkey'
                        ? 'Registering…'
                        : passkeyCount
                            ? 'Register Another Passkey'
                            : 'Register Passkey'}
                </button>
            </div>

            {/* Enrollment is per-person by construction: the secret has to reach
                that person's own phone, so nobody can register it for them. */}
            <p style={styles.adminNote}>
                Administrators: a second factor can only be registered by its owner, on
                their own device. Team Members shows who has one, and lets you require
                a member to register before they can use the console again.
            </p>
        </div>
    );
}

const styles = {
    header: { marginBottom: 28 },
    title: {
        margin: 0,
        fontSize: 22,
        fontWeight: 700,
        color: '#F1F5F9',
        letterSpacing: '-0.02em',
    },
    subtitle: {
        margin: '6px 0 0',
        fontSize: 14,
        color: '#B4C0D3',
    },
    card: {
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 12,
        padding: '28px 32px',
    },
    sectionHeader: {
        display: 'flex',
        gap: '14px',
        alignItems: 'flex-start',
        marginBottom: 20,
    },
    sectionTitle: {
        margin: '0 0 4px',
        fontSize: 16,
        fontWeight: 600,
        color: '#E2E8F0',
    },
    sectionDescription: {
        margin: 0,
        fontSize: 14,
        color: '#B4C0D3',
        lineHeight: '1.5',
    },
    registerBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 24px',
        fontSize: 14,
        fontWeight: 600,
        color: '#FFFFFF',
        backgroundColor: '#3B82F6',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'background-color 0.15s',
        fontFamily: 'inherit',
    },
    registerBtnDisabled: {
        backgroundColor: '#475569',
        cursor: 'not-allowed',
    },
    secondaryBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 18px',
        marginLeft: 10,
        fontSize: 14,
        fontWeight: 600,
        color: '#94A3B8',
        backgroundColor: 'transparent',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    error: {
        color: '#FCA5A5',
        fontSize: 13,
        margin: '0 0 14px',
        padding: '10px 14px',
        backgroundColor: 'rgba(239, 68, 68, 0.12)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        borderRadius: 8,
    },
    successBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        border: '1px solid rgba(34, 197, 94, 0.25)',
        borderRadius: 8,
        color: '#4ADE80',
        fontSize: 14,
        marginBottom: 20,
    },
    okBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px',
        backgroundColor: 'rgba(34, 197, 94, 0.08)',
        border: '1px solid rgba(34, 197, 94, 0.2)',
        borderRadius: 8,
        color: '#86EFAC',
        fontSize: 13,
        lineHeight: '1.5',
        marginBottom: 16,
    },
    warnBanner: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '12px 16px',
        backgroundColor: 'rgba(251, 191, 36, 0.08)',
        border: '1px solid rgba(251, 191, 36, 0.25)',
        borderRadius: 8,
        color: '#FCD34D',
        fontSize: 13,
        lineHeight: '1.5',
        marginBottom: 16,
    },
    infoBanner: {
        padding: '12px 16px',
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        borderRadius: 8,
        color: '#93C5FD',
        fontSize: 13,
        lineHeight: '1.5',
        marginBottom: 16,
    },
    adminNote: {
        margin: '20px 4px 0',
        fontSize: 13,
        color: '#B4C0D3',
        lineHeight: '1.6',
    },
};
